import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeftLong } from '@fortawesome/free-solid-svg-icons';
import PageLoading from '../../common/components/elements/PageLoading';
import { getCustomerAction } from '../../features/customers/customersActions';
import {
  ActiveCustomerDetailsStyles,
  TableRowStyle,
  CustomerDetailsHeader,
  ActionCard,
  SectionCard,
  SectionHeader,
  SectionBadge,
  CustomTable,
  EmptyState,
  EmptyStateIcon,
  BtnWhiteOutline,
  BadgeCustom,
  BackBtnCustom,
} from './ActiveCustomerDetails.styles';
import ErrorComponent from '../../common/components/ErrorComponent';
import SchedaAnagrafica from './parts/SchedaAnagrafica';
import ContactInformation from './parts/ContactInformation';

function CustomerInformationPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const {
    customer: {
      customer, insurance_requests, loading, contacts, error,
    },
  } = useSelector((store) => store.customers);

  useEffect(() => {
    getCustomerAction(customerId);
  }, []);

  const newQuotation = (productType) => {
    if (productType === 'auto') {
      navigate('/quote/auto', {
        state: {
          customerId,
        },
      });
    }
    if (productType === 'autocarro') {
      navigate('/quote/autocarro', {
        state: {
          customerId,
        },
      });
    }
    if (productType === 'profession') {
      navigate('/quote/profession', {
        state: {
          customerId,
        },
      });
    }
    if (productType === 'infortuni') {
      navigate('/quote/metlife/meno-male-gold', {
        state: {
          customerId,
        },
      });
    }
  };

  const sendToInsuranceRequestQuotationsList = (ir) => {
    if (ir.product_id === '1' || ir.product_id === '3') {
      navigate(`/quotes/${ir.request_token}`);
    } else if (ir.product_id === '2') {
      navigate(`/quotes/moto/${ir.request_token}`);
    } else if (ir.product_id === '4') {
      navigate(`/quotes/profession/${ir.request_token}`);
    } else if (ir.product_id === '5') {
      navigate(`/quotes/metlife/${ir.request_token}`);
    }
  };

  if (loading) {
    return <PageLoading />;
  }

  return error === null ? (
    <ActiveCustomerDetailsStyles>
      {/* Header Section */}
      <CustomerDetailsHeader>
        <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
          <div className="d-flex align-items-center">
            <BackBtnCustom id="back-button" onClick={() => navigate(-1)}>
              <FontAwesomeIcon icon={faArrowLeftLong} />
            </BackBtnCustom>

            <div>
              <h2 className="mb-1" style={{ fontWeight: '600', fontSize: '1.75rem' }}>
                {customer.gender !== 'G'
                  ? `${customer.name} ${customer.surname}`
                  : customer.business_name}
              </h2>
              <p className="mb-0" style={{ opacity: '0.9', fontSize: '0.9rem' }}>
                {customer.gender !== 'G' ? 'Persona Fisica' : 'Persona Giuridica'}
              </p>
            </div>
          </div>
          <div className="d-flex flex-column flex-md-row gap-3">
            <ActionCard>
              <p className="mb-2">Nuovo Preventivo</p>
              <div className="btn-group w-100 gap-2" role="group">
                <BtnWhiteOutline
                  type="button"
                  onClick={() => newQuotation('auto')}
                  style={{ fontSize: '0.85rem' }}
                >
                  Auto
                </BtnWhiteOutline>
                <BtnWhiteOutline
                  type="button"
                  onClick={() => newQuotation('autocarro')}
                  style={{ fontSize: '0.85rem' }}
                >
                  Autocarro
                </BtnWhiteOutline>
                <BtnWhiteOutline
                  type="button"
                  onClick={() => newQuotation('profession')}
                  style={{ fontSize: '0.85rem' }}
                >
                  Professionale
                </BtnWhiteOutline>
              </div>
            </ActionCard>
          </div>
        </div>
      </CustomerDetailsHeader>

      {/* Customer Information Cards */}
      <div className="row g-4 mb-4">
        <SchedaAnagrafica activeCustomer={customer} />
        <ContactInformation activeCustomer={customer} activeClientId={customerId} />
      </div>

      {/* Preventivi Fatti */}
      <SectionCard>
        <SectionHeader>
          <h5>Preventivi Fatti</h5>
          <SectionBadge>{insurance_requests.length}</SectionBadge>
        </SectionHeader>
        <div className="table-responsive">
          <CustomTable className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Prodotto</th>
                <th>Targa</th>
                <th>Data D'inizio</th>
                <th>Creato a</th>
              </tr>
            </thead>
            <tbody>
              {insurance_requests.length !== 0 ? (
                insurance_requests.map((request, index) => (
                  <TableRowStyle
                    key={index}
                    onClick={() => sendToInsuranceRequestQuotationsList(request)}
                  >
                    <td>
                      <BadgeCustom style={{ background: '#e3f2fd', color: '#1976d2' }}>
                        {request.request_token}
                      </BadgeCustom>
                    </td>
                    <td><strong>{request.product.name}</strong></td>
                    <td>{request.vehicle_plate || '-'}</td>
                    <td>{moment(request.policy_effective_date).format('DD.MM.YYYY')}</td>
                    <td>{moment(request.created_at).format('DD.MM.YYYY HH:mm')}</td>
                  </TableRowStyle>
                ))
              ) : (
                <tr>
                  <td colSpan={5}>
                    <EmptyState>
                      <EmptyStateIcon>📋</EmptyStateIcon>
                      <p className="mb-0">Nessun Preventivo Trovato!</p>
                    </EmptyState>
                  </td>
                </tr>
              )}
            </tbody>
          </CustomTable>
        </div>
      </SectionCard>

      {/* Storico Contatti */}
      <SectionCard>
        <SectionHeader>
          <h5>Storico Contatti</h5>
          <SectionBadge>{contacts.length}</SectionBadge>
        </SectionHeader>
        <div className="table-responsive">
          <CustomTable className="table">
            <thead>
              <tr>
                <th>Intermediario</th>
                <th>Metodo Di Contatto</th>
                <th>Messaggio</th>
                <th>Data</th>
              </tr>
            </thead>
            <tbody>
              {contacts.length > 0 ? (
                contacts.map((contact, index) => (
                  <TableRowStyle key={index}>
                    <td><strong>{`${contact.broker_name} ${contact.broker_surname}`}</strong></td>
                    <td>
                      <BadgeCustom
                        style={{
                          background: contact.contact_type === 1 ? '#e3f2fd' : '#f3e5f5',
                          color: contact.contact_type === 1 ? '#1976d2' : '#7b1fa2',
                        }}
                      >
                        {contact.contact_type === 1 ? '📧 Email' : '💬 SMS'}
                      </BadgeCustom>
                    </td>
                    <td>{contact.message}</td>
                    <td>{moment(contact.created_at).toDate().toLocaleDateString('it-IT')}</td>
                  </TableRowStyle>
                ))
              ) : (
                <tr>
                  <td colSpan={4}>
                    <EmptyState>
                      <EmptyStateIcon>📞</EmptyStateIcon>
                      <p className="mb-0">Nessuno Storico Contatti Trovato!</p>
                    </EmptyState>
                  </td>
                </tr>
              )}
            </tbody>
          </CustomTable>
        </div>
      </SectionCard>
    </ActiveCustomerDetailsStyles>
  ) : (
    <ErrorComponent error={error} />
  );
}

export default CustomerInformationPage;
